import React, { useState, useRef, useEffect, useCallback } from 'react';
import './App.css';
import FiveStageJournal from './FiveStageJournal';
import BreathingExercise from './BreathingExercise';
import ReflectOnToday from './components/ReflectOnToday';
import Memorial from './components/Memorial';
import { AuthProvider, useAuth } from './components/AuthProvider';
import Login from './components/Login';
import SignUp from './components/SignUp';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import UserProfile from './components/UserProfile';
import JournalLanding from './components/JournalLanding';
import Icon from './components/ui/Icon';
import PastelButton from './components/ui/PastelButton';
import iconPreloader from './components/ui/iconPreloader';
import SendTreatsModal from './components/treats/SendTreatsModal';
import TreatAnimation from './components/treats/TreatAnimation';
import AngelPetReaction from './components/treats/AngelPetReaction';
import WelcomeBubble from './components/WelcomeBubble';
import { fetchUserPets, clearPetCache, addPetToCache } from './utils/petApiCache';

// Main App Component (wrapped with auth)
function AppContent() {
  const { user, loading: authLoading, isPasswordRecovery } = useAuth();
  
  // ✅ Declare ALL hooks first, before any conditional returns
  const [activeSection, setActiveSection] = useState('home');
  
  // Auth view state for login/signup/forgot-password/reset-password
  const [authView, setAuthView] = useState('login');
  const [uploadedImage, setUploadedImage] = useState(null);
  const [angelPet, setAngelPet] = useState(null);
  const [petName, setPetName] = useState('');
  const [showInteractions, setShowInteractions] = useState(false);
  const [flowers, setFlowers] = useState([]);
  const [letters, setLetters] = useState([]);
  const [currentLetter, setCurrentLetter] = useState('');
  const [petId, setPetId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [transformationError, setTransformationError] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  
  // Home page internal state management
  const [homeView, setHomeView] = useState('main'); // 'main', 'letter', 'treats', 'messages'
  
  // Journal page internal state management
  const [journalView, setJournalView] = useState('main'); // 'main', 'healing', 'reflect'
  
  // Breathe page internal state management
  const [breatheView, setBreatheView] = useState('main'); // 'main', 'exercise'
  
  // Breathe page view renderers
  const renderBreatheMainView = () => (
    <div className="breathe-section relative w-full h-full overflow-hidden">
      {/* Sun + Clouds Layer - Background */}
      <img 
        src="/assets/sun.png" 
        className="absolute top-4 left-4 w-24 h-24 animate-gentle-float" 
        style={{ zIndex: 5 }}
        alt="Sun"
      />
      
      {/* Grass Base Layer - Bottom */}
      <img 
        src="/assets/grass.png" 
        className="absolute bottom-0 left-0 w-full h-32 object-cover object-top" 
        style={{ zIndex: 8 }}
        alt="Grass field"
      />
      
      {/* Angel Pet - Center-bottom, below rainbow */}
      {angelPet && (
        <div className="angel-pet-container" style={{ zIndex: 15 }}>
          <div className="relative">
            {/* Rainbow positioned relative to angel avatar */}
            <img
              src="/assets/rainbow.png"
              alt="Rainbow"
              className="absolute left-1/2 -translate-x-1/2 pointer-events-none 
                        w-[600px] h-[300px] scale-[4.5]"
              style={{
                zIndex: 10,
                objectFit: 'contain',
                transformOrigin: '50% 100%',
                bottom: 'calc(100% - 30px)'
              }}
            />
            <img 
              src={angelPet} 
              className="w-40 h-40 rounded-full shadow-xl animate-breathing-glow object-cover border-4 border-white/30 relative"
              alt="Angel Pet"
              style={{ zIndex: 20 }}
            />
            <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-200/20 via-purple-200/20 to-pink-200/20 animate-pulse" style={{ zIndex: 5 }}></div>
          </div>
        </div>
      )}
      
      {/* Floating sparkles for ambiance - Above angel pet */}
      <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 25 }}>
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            className="absolute animate-float-slow"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${20 + Math.random() * 50}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${4 + Math.random() * 2}s`,
            }}
          >
            <span className="text-white/60 text-sm"><Icon name="sparkle" size="xs" /></span>
          </div>
        ))}
      </div>
      
      {/* Start Button - Floating above nav bar, highest z-index */}
      <PastelButton 
        className="absolute bottom-12 left-1/2 -translate-x-1/2 backdrop-blur-sm shadow-lg border-2 border-white/50"
        style={{ zIndex: 30 }}
        onClick={() => setBreatheView('exercise')}
        color="mint"
        animate={false}
      >
        <Icon name="sparkle" size="sm" /> Start Breathing
      </PastelButton>
    </div>
  );

  const renderBreatheExerciseView = () => (
    <BreathingExercise
      petName={petName}
      petId={petId}
      angelImage={angelPet}
      onClose={() => setBreatheView('main')}
      onComplete={() => {
        console.log('Breathing exercise completed');
        setSuccessMessage({
          title: 'Breathing session complete!',
          description: `You've taken peaceful breaths with Angel ${petName}.`
        });
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 4000);

        // Set reactive message for breathing completion
        const breathingReactiveMessages = [
          "Our synchronized breathing fills the air with love! 🌬️\nI felt every peaceful breath with you!",
          "Breathing together across dimensions! ✨\nYour calm energy reaches my heavenly lungs!",
          "Each breath we shared was pure meditation! 🧘‍♀️\nYour serenity makes the clouds dance!",
          "Inhaling peace, exhaling love together! 💙\nOur breathing harmony is magical!",
          "Your mindful breaths create rainbow ripples! 🌈\nI'm floating on your peaceful energy!",
          "Breathing in unison across the bridge! 🌉\nYour tranquility fills my angel heart!"
        ];

        const randomBreathingMessage = breathingReactiveMessages[Math.floor(Math.random() * breathingReactiveMessages.length)];
        
        // Show reactive message after success message
        setTimeout(() => {
          setReactiveMessage(randomBreathingMessage);
        }, 4500);
      }}
    />
  );
  
  // Journal page view renderers
  const renderJournalMainView = () => (
    <JournalLanding
      petName={petName}
      angelPet={angelPet}
      onTileClick={handleJournalTileClick}
    />
  );

  const renderJournalHealingView = () => (
    <FiveStageJournal
      petName={petName}
      petId={petId}
      angelPet={angelPet}
      onClose={() => setJournalView('main')}
      onComplete={(responses) => {
        console.log('Five-stage journal completed:', responses);
        setSuccessMessage({
          title: 'Healing journey complete!',
          description: 'Your five-stage healing journey has been beautifully documented.'
        });
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 4000);

        // Set reactive message for journal completion
        const journalReactiveMessages = [
          "Your healing journey touches my angel heart! 💙\nI'm so proud of your courage and love!",
          "Each stage of grief you've explored shines with wisdom! ✨\nYour growth makes paradise even brighter!",
          "Walking through emotions with such bravery! 🌟\nI feel your healing energy from the clouds!",
          "Your journal reflects a beautiful, healing soul! 🌈\nEvery word helps both our hearts grow!",
          "Documenting love and loss with such grace! 💕\nYour journey inspires all the angels here!",
          "Each honest feeling you've shared is sacred! 🕊️\nYour healing path lights up the rainbow bridge!"
        ];

        const randomJournalMessage = journalReactiveMessages[Math.floor(Math.random() * journalReactiveMessages.length)];
        
        // Show reactive message after success message
        setTimeout(() => {
          setReactiveMessage(randomJournalMessage);
        }, 4500);
      }}
    />
  );

  const renderJournalReflectView = () => (
    <ReflectOnToday 
      petName={petName} 
      angelPet={angelPet}
      onClose={() => setJournalView('main')} 
      onSaveToMemorial={addReflectionToMemorial}
    />
  );
  
  // Home page view renderers
  const renderHomeMainView = () => (
    <>
      {!angelPet ? (
        <div className="upload-section">
          <div className="upload-card">
            <h2>Begin Your Rainbow Bridge Search</h2>
            <p>Upload a photo and tell us your pet's name. We'll search the Rainbow Bridge to help you reunite…</p>
            <input
              id="pet-name"
              name="pet-name"
              type="text"
              placeholder="What was your pet's name?"
              value={petName}
              onChange={(e) => setPetName(e.target.value)}
              className="pet-name-input"
            />
            <div className="upload-area" onClick={() => !transformationError && fileInputRef.current?.click()}>
              {loading ? (
                <div>
                  <div className="upload-icon"><Icon name="sparkle" size="xl" animate /></div>
                  <p>Searching the Rainbow Bridge…</p>
                  <small>Your love is calling out across the clouds…</small>
                </div>
              ) : transformationError ? (
                <div className="error-state">
                  <div className="upload-icon"><Icon name="cloud" size="xl" /></div>
                  <p className="error-message">Uh oh, we are having some issues talking to the Rainbow Bridge, please try again later</p>
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRetryTransformation();
                    }}
                    disabled={isRetrying}
                    className="retry-button"
                  >
                    {isRetrying ? 'Trying…' : 'Try Again'}
                  </button>
                </div>
              ) : (
                <div>
                  <div className="upload-icon"><Icon name="camera" size="xl" /></div>
                  <p>Click to upload your pet's photo</p>
                  <small>We'll help you find them across the Rainbow Bridge.</small>
                </div>
              )}
              <input
                id="pet-image-upload"
                name="pet-image-upload"
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                style={{ display: 'none' }}
                disabled={loading || transformationError}
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="heaven-home">
          {/* Emotionally Welcoming Angel Pet Section */}
          <div className="angel-pet-display relative">
            {/* Admin Controls - Subtle and tucked away */}
            {/* <div className="absolute top-2 right-2 flex gap-2" style={{ zIndex: 9999 }}>
              <button 
                onClick={() => {
                  clearLocalStorage();
                  window.location.reload();
                }}
                className="btn-new-pet text-xs px-3 py-2 bg-white/80 backdrop-blur-sm rounded-full text-gray-700 hover:bg-white/90 transition-all duration-200 shadow-lg border border-white/50"
                title="Create a new angel pet"
                style={{ pointerEvents: 'auto' }}
              >
                New Pet
              </button>
              <button 
                onClick={() => {
                  clearLocalStorage();
                  setAngelPet(null);
                  setShowInteractions(false);
                  setPetId(null);
                  setPetName('');
                  console.log('🧹 Storage cleared - ready for fresh AI transformation!');
                }}
                className="btn-clear-storage text-xs px-3 py-2 bg-white/80 backdrop-blur-sm rounded-full text-gray-700 hover:bg-white/90 transition-all duration-200 shadow-lg border border-white/50"
                title="Clear storage and start fresh"
                style={{ pointerEvents: 'auto' }}
              >
                Reset
              </button>
            </div> */}

            {/* Main Welcome Bubble Component with Dual-Layer Speech */}
            <WelcomeBubble 
              petName={petName} 
              angelPet={angelPet} 
              reactiveMessage={reactiveMessage}
              onReactiveMessageComplete={() => setReactiveMessage(null)}
            />
          </div>
          {showInteractions && (
            <div className="interactions px-4 mt-6">
              <div className="interaction-buttons flex flex-col sm:flex-row gap-3 max-w-md mx-auto justify-center">
                <PastelButton 
                  onClick={() => setHomeView('treats')} 
                  color="rose"
                  className="flex-1"
                >
                  Send Heavenly Treats <img src="/assets/givetreats.png" alt="Give treats" className="inline w-6 h-6 ml-1" />
                </PastelButton>
                <PastelButton 
                  onClick={() => setHomeView('letter')} 
                  color="sky"
                  className="flex-1"
                >
                  Write a Love Letter ✉️
                </PastelButton>
                <PastelButton 
                  onClick={() => setHomeView('messages')} 
                  color="purple"
                  className="flex-1 relative"
                >
                  Angel Messages 💌
                  {unreadCount > 0 && (
                    <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                      {unreadCount}
                    </span>
                  )}
                </PastelButton>
              </div>
            </div>
          )}
          {/* Floating treats */}
          {flowers.map(flower => (
            <div
              key={flower.id}
              className="absolute pointer-events-none animate-float"
              style={{ left: `${flower.x}%`, top: `${flower.y}%` }}
            >
              <img src="/assets/treats.png" alt="Treat" className="w-12 h-12" />
            </div>
          ))}
        </div>
      )}
    </>
  );

  const renderHomeLetterView = () => (
    <>
      {/* Header with back button */}
      <div className="relative text-center mb-6 pt-4">
        <button
          onClick={() => setHomeView('main')}
          className="absolute -top-4 -left-4 bg-white/80 backdrop-blur-sm text-gray-600 px-3 py-2 rounded-xl font-medium hover:bg-white/90 transition-all duration-300 shadow-md text-sm"
        >
          ← Back
        </button>
        
        <h1 className="text-xl md:text-2xl font-bold text-gray-700 mb-2 flex items-center justify-center gap-2">
          <Icon name="sparkle" size="md" color="primary" animate />
          Write a Letter to Angel {petName || 'Pet'}
        </h1>
        <p className="text-gray-600 text-sm">
          Share your thoughts and feelings with your beloved angel
        </p>
        <div className="w-24 h-px bg-white/40 mx-auto mt-3"></div>
      </div>

      {/* Letter Form */}
      <div className="max-w-sm mx-auto px-4">
        <div className="bg-gradient-to-br from-pink-100/80 to-purple-100/80 backdrop-blur-sm rounded-3xl p-6 shadow-lg border border-white/30">
          <textarea
            id="letter-content"
            name="letter-content"
            value={currentLetter}
            onChange={(e) => setCurrentLetter(e.target.value)}
            placeholder="Dear Angel, I want to tell you..."
            className="w-full h-64 sm:h-80 p-4 border border-pink-200 rounded-2xl focus:ring-2 focus:ring-pink-300 focus:border-transparent resize-none bg-white/80 backdrop-blur-sm text-gray-700 placeholder-gray-400 mb-6"
          />
          
          <div className="flex gap-3 justify-center">
            <PastelButton 
              onClick={() => setHomeView('main')} 
              color="gray"
              className="flex-1"
            >
              Cancel
            </PastelButton>
            <PastelButton 
              onClick={sendLetter} 
              color="sky"
              className="flex-1"
              disabled={loading}
            >
              {loading ? 'Sending...✨' : 'Send Letter ✉️'}
            </PastelButton>
          </div>
        </div>
      </div>
    </>
  );

  const renderHomeTreatsView = () => (
    <SendTreatsModal 
      isOpen={true} 
      onClose={() => setHomeView('main')} 
      onSendTreat={sendTreat} 
      petName={petName} 
      angelPet={angelPet} 
    />
  );

  // Helper function to format dates in a friendly way
  const formatExchangeDate = (dateValue) => {
    if (!dateValue) return '';
    const date = new Date(dateValue);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  // Create letter exchanges by pairing angel responses with their original letters
  // TODO: Future migration - add exchange_id to backend for more reliable pairing
  // Currently using letter_id from angel_response to find the matching letter
  const createLetterExchanges = () => {
    return angelResponses
      .slice()
      .sort((a, b) => {
        const timeA = new Date(a.timestamp || a.created_at || a.date);
        const timeB = new Date(b.timestamp || b.created_at || b.date);
        return timeB - timeA;
      })
      .map((response) => {
        // Find the corresponding user letter by letter_id
        const userLetter = letters.find(letter => letter.id === response.letter_id);
        return {
          id: response.id,
          userLetter: userLetter ? {
            content: userLetter.content,
            created_at: userLetter.timestamp || userLetter.created_at || userLetter.date,
          } : null,
          angelReply: {
            content: response.content,
            created_at: response.timestamp || response.created_at || response.date,
            status: response.status || 'ready',
            read: response.read,
          },
          // Keep original response for actions
          originalResponse: response,
        };
      });
  };

  const renderHomeMessagesView = () => {
    const exchanges = createLetterExchanges();
    
    return (
    <div className="angel-messages-page relative min-h-full">
      {/* Grain/noise overlay */}
      <div 
        className="absolute inset-0 pointer-events-none opacity-[0.03] z-0"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
        }}
        aria-hidden="true"
      />
      
      {/* Radial glow behind main content */}
      <div 
        className="absolute top-32 left-1/2 -translate-x-1/2 w-96 h-96 pointer-events-none z-0"
        style={{
          background: 'radial-gradient(circle, rgba(168, 85, 247, 0.12) 0%, rgba(236, 72, 153, 0.08) 40%, transparent 70%)',
        }}
        aria-hidden="true"
      />

      {/* Decorative sparkles - slow drift */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-0 motion-reduce:hidden" aria-hidden="true">
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            className="absolute animate-sparkle-drift"
            style={{
              left: `${15 + i * 18}%`,
              bottom: '-20px',
              animationDelay: `${i * 1.5}s`,
              animationDuration: `${8 + i}s`,
              opacity: 0.4,
            }}
          >
            <Icon name="sparkle" size="xs" color={['#c084fc', '#f9a8d4', '#93c5fd', '#fcd34d', '#a5b4fc'][i]} />
          </div>
        ))}
      </div>

      {/* Header with back button */}
      <div className="relative text-center mb-6 pt-10 z-10">
        <button
          onClick={() => setHomeView('main')}
          className="absolute top-0 left-0 bg-white/70 backdrop-blur-md text-gray-600 px-4 py-2 rounded-xl font-medium hover:bg-white/85 hover:-translate-y-0.5 active:scale-[0.98] transition-all duration-200 shadow-md text-sm border border-white/40"
        >
          ← Back
        </button>
        
        <h1 className="text-xl md:text-2xl font-bold text-gray-700 tracking-tight flex items-center justify-center gap-2">
          <Icon name="sparkle" size="md" color="primary" animate />
          Messages of Love
        </h1>
        <p className="text-xs text-gray-500/80 mt-1 font-light tracking-wide">
          Your letters and loving replies from Angel {petName || 'Pet'}
        </p>
        <div className="w-24 h-px bg-white/40 mx-auto mt-3"></div>
      </div>

      {/* Messages List - Glass Panel Container */}
      <div className="max-w-lg mx-auto px-3 pb-8 relative z-10">
        {exchanges.length === 0 ? (
          <div className="bg-white/25 backdrop-blur-md rounded-3xl p-8 shadow-[0_16px_40px_rgba(0,0,0,0.10)] border border-white/30 text-center relative overflow-hidden">
            {/* Inner highlight */}
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-white/30 via-white/10 to-transparent pointer-events-none" />
            <div className="relative z-10">
              <div className="text-5xl mb-4">💌</div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">No messages yet</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Send a letter to Angel {petName || 'your pet'} and they'll respond with love! ✨
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-5">
            {exchanges.map((exchange) => (
                <div 
                  key={exchange.id} 
                  className={`relative bg-white/25 backdrop-blur-md rounded-3xl p-5 shadow-[0_16px_40px_rgba(0,0,0,0.10)] border transition-all duration-300 hover:shadow-[0_20px_50px_rgba(0,0,0,0.12)] hover:-translate-y-1 overflow-hidden ${
                    !exchange.angelReply.read 
                      ? 'border-pink-200/50 ring-2 ring-pink-300/30' 
                      : 'border-white/30'
                  }`}
                >
                  {/* Inner highlight */}
                  <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-white/30 via-white/10 to-transparent pointer-events-none" />
                  
                  {/* ===== SECTION A: From Your Heart (User Letter) ===== */}
                  <div className="relative z-10 mb-4">
                    {/* Section Header */}
                    <div className="flex items-center gap-2 mb-3">
                      <Icon name="heart" size="sm" color="#ec4899" />
                      <h4 className="text-sm font-semibold text-pink-600/90 tracking-wide">From Your Heart</h4>
                    </div>
                    
                    {/* User Letter Content */}
                    {exchange.userLetter ? (
                      <div className="bg-pink-50/40 backdrop-blur-sm rounded-2xl p-4 border border-pink-100/30">
                        <p className="text-gray-600 leading-6 whitespace-pre-wrap text-sm italic">
                          "{exchange.userLetter.content}"
                        </p>
                        <p className="text-xs text-gray-400 mt-3 text-right">
                          {formatExchangeDate(exchange.userLetter.created_at)}
                        </p>
                      </div>
                    ) : (
                      // TODO: Handle orphaned responses (rare edge case where letter was deleted)
                      <div className="bg-pink-50/30 backdrop-blur-sm rounded-2xl p-4 border border-pink-100/20">
                        <p className="text-gray-400 text-sm italic">
                          Original letter not available
                        </p>
                      </div>
                    )}
                  </div>

                  {/* ===== DIVIDER ===== */}
                  <div className="relative z-10 my-4">
                    <div className="h-px bg-gradient-to-r from-transparent via-purple-200/50 to-transparent" />
                  </div>

                  {/* ===== SECTION B: From Angel (Reply) ===== */}
                  <div className="relative z-10">
                    {/* Section Header with Avatar */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        {/* Avatar with angel pet image and glow */}
                        <div className="relative">
                          {angelPet ? (
                            <img 
                              src={angelPet} 
                              alt={`Angel ${petName || 'Pet'}`}
                              className="w-9 h-9 rounded-full object-cover shadow-lg border-2 border-white/50"
                              style={{
                                boxShadow: '0 4px 12px rgba(168, 85, 247, 0.25), 0 0 15px rgba(236, 72, 153, 0.12)',
                              }}
                            />
                          ) : (
                            <div 
                              className="w-9 h-9 rounded-full bg-gradient-to-br from-pink-400 via-purple-400 to-indigo-400 flex items-center justify-center text-white font-bold text-sm shadow-lg"
                              style={{
                                boxShadow: '0 4px 12px rgba(168, 85, 247, 0.25), 0 0 15px rgba(236, 72, 153, 0.12)',
                              }}
                            >
                              {petName ? petName.charAt(0).toUpperCase() : 'A'}
                            </div>
                          )}
                          {/* Subtle glow ring */}
                          <div 
                            className="absolute inset-0 rounded-full opacity-40"
                            style={{
                              boxShadow: '0 0 12px 2px rgba(168, 85, 247, 0.2)',
                            }}
                            aria-hidden="true"
                          />
                        </div>
                        <div>
                          <h4 className="text-sm font-semibold text-purple-600/90 tracking-wide">From Angel {petName || 'Pet'}</h4>
                          <p className="text-xs text-gray-400">
                            {formatExchangeDate(exchange.angelReply.created_at)}
                          </p>
                        </div>
                      </div>
                      {!exchange.angelReply.read && (
                        <span className="bg-gradient-to-r from-pink-500 to-rose-500 text-white text-xs px-2.5 py-0.5 rounded-full font-medium shadow-md">
                          New
                        </span>
                      )}
                    </div>
                    
                    {/* Angel Reply Content */}
                    {exchange.angelReply.content ? (
                      <div className="bg-white/45 backdrop-blur-sm rounded-2xl p-4 border border-white/40 shadow-sm">
                        <p className="text-gray-700 leading-6 whitespace-pre-wrap text-[15px]">
                          {exchange.angelReply.content}
                        </p>
                      </div>
                    ) : (
                      // Placeholder for pending replies
                      <div className="bg-purple-50/30 backdrop-blur-sm rounded-2xl p-4 border border-purple-100/20">
                        <p className="text-purple-400/80 text-sm italic flex items-center gap-2">
                          <Icon name="sparkle" size="xs" color="#a855f7" animate />
                          Angel {petName || 'Pet'} is writing back…
                        </p>
                      </div>
                    )}
                  </div>
                  
                  {/* Action Buttons - Glass Action Bar */}
                  <div className="relative z-10 flex items-center justify-end mt-4">
                    <div className="flex items-center gap-2 bg-white/30 backdrop-blur-sm rounded-xl p-1.5 border border-white/25">
                      {/* Save to Garden - Primary */}
                      <button
                        onClick={() => addAngelMessageToMemorial(exchange.originalResponse)}
                        className="text-sm bg-purple-100/80 hover:bg-purple-200/90 text-purple-700 px-3 py-1.5 rounded-lg transition-all duration-200 flex items-center gap-1.5 hover:-translate-y-0.5 active:scale-[0.98] shadow-sm"
                        title="Add to Memorial Garden"
                      >
                        <Icon name="heart" size="xs" color="#7c3aed" />
                        Save to Garden
                      </button>
                      
                      {!exchange.angelReply.read && (
                        <button
                          onClick={() => markResponseAsRead(exchange.originalResponse.id)}
                          className="text-sm text-pink-600 hover:text-pink-700 font-medium transition-all duration-200 bg-pink-50/80 hover:bg-pink-100/90 px-3 py-1.5 rounded-lg hover:-translate-y-0.5 active:scale-[0.98]"
                        >
                          Mark read
                        </button>
                      )}
                      
                      {/* Delete - Secondary */}
                      <button
                        onClick={() => deleteAngelResponse(exchange.originalResponse.id)}
                        className="text-sm bg-white/50 hover:bg-red-50/80 text-gray-400 hover:text-red-500 px-3 py-1.5 rounded-lg transition-all duration-200 border border-white/30 hover:border-red-200/50 hover:-translate-y-0.5 active:scale-[0.98]"
                        title="Delete message"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );};
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState({
    title: '',
    description: ''
  });
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const [journalEntries, setJournalEntries] = useState([]);
  
  // New treat system state
  const [treatAnimation, setTreatAnimation] = useState(null);
  const [petReaction, setPetReaction] = useState(null);
  
  // Dual-layer speech system state
  const [reactiveMessage, setReactiveMessage] = useState(null);
  
  // Angel responses state
  const [angelResponses, setAngelResponses] = useState([]);
  const isInitialAngelResponseLoad = useRef(true); // Track if this is the first load
  
  // Delete confirmation modal state
  const [deleteConfirmation, setDeleteConfirmation] = useState({ show: false, responseId: null });
  
  // Calculate unread count from actual messages (not a separate counter)
  const unreadCount = angelResponses.filter(r => !r.read).length;

  // Get backend URL from environment
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  // ✅ Declare ALL useEffect hooks before conditional returns
  // Show welcome message for new sign-ins (only once per session)
  useEffect(() => {
    if (user) {
      const welcomeShown = sessionStorage.getItem('welcomeMessageShown');
      
      if (!welcomeShown) {
        console.log('🎉 User authenticated! Showing welcome message for:', user.email);
        sessionStorage.setItem('welcomeMessageShown', 'true');
        
        setSuccessMessage({
          title: 'Welcome to Rainbow Bridge!',
          description: `Signed in as ${user.email}`
        });
        setShowSuccessMessage(true);
        setTimeout(() => {
          setShowSuccessMessage(false);
        }, 4000);
      }
    } else {
      // Clear the welcome flag when user logs out
      sessionStorage.removeItem('welcomeMessageShown');
    }
  }, [user]);
  
  // Track if pet loading has been initiated this session
  const petLoadInitiated = useRef(false);
  
  // Load existing pet data on app start and initialize icon preloader
  // Uses ref to prevent duplicate calls from StrictMode or auth state changes
  useEffect(() => {
    if (user && !petLoadInitiated.current) {
      petLoadInitiated.current = true;
      console.log('[App] 🚀 loadExistingPet triggered');
      console.log('[App] Backend URL:', BACKEND_URL);
      console.log('[App] User authenticated:', user.email);
      console.log('[App] User ID:', user.id);
      loadExistingPet();
      
      // Initialize icon preloader for better performance
      iconPreloader.preloadCriticalIcons();
    }
    
    // Reset the ref when user logs out
    if (!user) {
      petLoadInitiated.current = false;
    }
  }, [user]);

  useEffect(() => {
    // Fetch journal entries when petId or section changes
    if (activeSection === 'memorial' && petId) {
      fetch(`${BACKEND_URL}/api/pets/${petId}/journal`)
        .then(res => res.json())
        .then(data => {
          setJournalEntries(data.entries || []);
        })
        .catch(err => console.error('Failed to fetch journal entries:', err));
    }
  }, [activeSection, petId]);

  // Poll for angel responses every 30 seconds
  useEffect(() => {
    if (petId) {
      // Check immediately
      checkForAngelResponses(petId);
      
      // Set up polling interval
      const interval = setInterval(() => {
        checkForAngelResponses(petId);
      }, 30000); // Check every 30 seconds
      
      return () => clearInterval(interval);
    }
  }, [petId]);

  // Close all modals when switching navigation
  useEffect(() => {
    setHomeView('main');
    setJournalView('main');
    setBreatheView('main');
  }, [activeSection]);

  // ✅ Define all functions BEFORE conditional returns
  const loadExistingPet = async () => {
    console.log('[loadExistingPet] 🔍 Function called');
    
    try {
      // PRIORITY 1: Load from localStorage IMMEDIATELY for instant UI
      // This gives instant perceived performance
      const savedPetId = localStorage.getItem('petHeavenPetId');
      const savedPetName = localStorage.getItem('petHeavenPetName');
      const savedAngelImage = localStorage.getItem('petHeavenAngelImage');
      
      if (savedPetId && savedPetName && savedAngelImage) {
        console.log('[loadExistingPet] ⚡ Instant load from localStorage:', savedPetName);
        setPetId(savedPetId);
        setPetName(savedPetName);
        setAngelPet(savedAngelImage);
        setShowInteractions(true);
        
        // Load letters immediately (they're usually small)
        loadLetters(savedPetId);
        checkForAngelResponses(savedPetId);
        
        // BACKGROUND SYNC: Verify with server and update if needed
        // Don't await - let it happen in background
        if (user?.id) {
          backgroundSyncPet(user.id, savedPetId);
        }
        return;
      }
      
      // PRIORITY 2: No localStorage cache - fetch from server
      if (user?.id) {
        console.log('[loadExistingPet] 🔍 No cache, fetching from server for user:', user.id);
        
        const data = await fetchUserPets(user.id, BACKEND_URL);
        
        if (data.pets && data.pets.length > 0) {
          const pet = data.pets[0];
          console.log('[loadExistingPet] ✅ Found pet from server:', pet.name);
          
          setPetId(pet.id);
          setPetName(pet.name);
          setAngelPet(pet.angel_image);
          setShowInteractions(true);
          
          // Update localStorage for next instant load
          localStorage.setItem('petHeavenPetId', pet.id);
          localStorage.setItem('petHeavenPetName', pet.name);
          localStorage.setItem('petHeavenAngelImage', pet.angel_image);
          
          await loadLetters(pet.id);
          await checkForAngelResponses(pet.id);
        } else {
          console.log('[loadExistingPet] ℹ️ No pets found on server');
        }
      }
    } catch (error) {
      console.error('[loadExistingPet] ❌ Error loading existing pet:', error);
    }
  };
  
  // Background sync to verify localStorage data is still valid
  const backgroundSyncPet = async (userId, cachedPetId) => {
    try {
      console.log('[backgroundSync] 🔄 Verifying cached pet in background...');
      const data = await fetchUserPets(userId, BACKEND_URL, { forceRefresh: true });
      
      if (data.pets && data.pets.length > 0) {
        const serverPet = data.pets[0];
        
        // Check if server has a different/newer pet
        if (serverPet.id !== cachedPetId) {
          console.log('[backgroundSync] 🔄 Server has different pet, updating...');
          setPetId(serverPet.id);
          setPetName(serverPet.name);
          setAngelPet(serverPet.angel_image);
          
          localStorage.setItem('petHeavenPetId', serverPet.id);
          localStorage.setItem('petHeavenPetName', serverPet.name);
          localStorage.setItem('petHeavenAngelImage', serverPet.angel_image);
        } else {
          console.log('[backgroundSync] ✅ Cache is valid');
        }
      }
    } catch (error) {
      console.warn('[backgroundSync] ⚠️ Background sync failed (using cached data):', error.message);
    }
  };

  const clearLocalStorage = () => {
    localStorage.removeItem('petHeavenPetId');
    localStorage.removeItem('petHeavenPetName');
    localStorage.removeItem('petHeavenAngelImage');
    // Also clear the API cache
    clearPetCache();
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setTransformationError(false); // Reset error state on new upload
      const reader = new FileReader();
      reader.onload = (e) => {
        setUploadedImage(e.target.result);
        console.log("Uploading image:", e.target.result.substring(0, 100)); // Debug: log image data URL prefix
        createAngelPet(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRetryTransformation = async () => {
    if (isRetrying || loading || !uploadedImage) return; // Prevent multiple clicks
    
    setIsRetrying(true);
    setTransformationError(false);
    await createAngelPet(uploadedImage);
    setIsRetrying(false);
  };

  const createAngelPet = async (imageDataUrl) => {
    try {
      setLoading(true);
      console.log('🚀 Starting AI transformation for:', petName);
      console.log('🔗 Backend URL:', BACKEND_URL);
      console.log('🔐 User:', user.email);
      console.log('Sending image data (prefix):', imageDataUrl.substring(0, 100)); // Debug
      // First try AI transformation
      const response = await fetch(`${BACKEND_URL}/api/pets-ai`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: petName,
          original_image: imageDataUrl,
          user_id: user.id // Add user ID to link pets to users
        })
      });

      console.log('📡 API Response status:', response.status, response.statusText);

      if (response.ok) {
        const data = await response.json();
        console.log('🤖 AI API Response:', data);
        setPetId(data.pet_id);
        
        // Check if we got an AI-generated angel image AND AI transformation was successful
        if (data.angel_image && data.angel_image.startsWith('data:image/') && data.ai_transformed === true) {
          setAngelPet(data.angel_image);
          console.log("✨ SUCCESS: Using AI-generated angel image!");
          console.log("🖼️ Angel image length:", data.angel_image.length);
          
          // Save to localStorage for persistence
          localStorage.setItem('petHeavenPetId', data.pet_id);
          localStorage.setItem('petHeavenPetName', petName);
          localStorage.setItem('petHeavenAngelImage', data.angel_image);
          
          // Load existing letters
          await loadLetters(data.pet_id);
          setShowInteractions(true);
        } else {
          // AI transformation failed or returned invalid image
          if (data.ai_transformed === false) {
            console.log("❌ AI transformation failed");
            console.log("🔍 Backend message:", data.message);
          } else {
            console.log("❌ AI transformation returned invalid image");
            console.log("🔍 Received angel_image:", data.angel_image ? data.angel_image.substring(0, 100) + '...' : 'null');
          }
          setTransformationError(true);
        }
      } else {
        // If API fails, show error
        console.log("❌ AI API failed with status:", response.status);
        const errorText = await response.text();
        console.error("❌ Error response from backend:", errorText);
        setTransformationError(true);
      }
    } catch (error) {
      console.error('❌ Error creating angel pet with AI:', error);
      setTransformationError(true);
    } finally {
      setLoading(false);
    }
  };

  const createCanvasAngelPet = async (imageDataUrl) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    return new Promise((resolve) => {
      img.onload = async () => {
        // Set canvas size
        canvas.width = 400;
        canvas.height = 400;
        
        // Clear canvas
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        
        // Calculate image dimensions to fit canvas while maintaining aspect ratio
        const scale = Math.min(canvas.width / img.width, canvas.height / img.height);
        const x = (canvas.width / 2) - (img.width / 2) * scale;
        const y = (canvas.height / 2) - (img.height / 2) * scale;
        
        // Apply angel filters
        ctx.filter = 'brightness(1.3) contrast(0.9) sepia(0.2) saturate(0.8)';
        ctx.drawImage(img, x, y, img.width * scale, img.height * scale);
        
        // Add angel wings
        ctx.filter = 'none';
        drawAngelWings(ctx, canvas.width, canvas.height);
        
        // Add halo
        drawHalo(ctx, canvas.width / 2, y + 20);
        
        // Convert to data URL and save
        const angelImageUrl = canvas.toDataURL();
        setAngelPet(angelImageUrl);
        
        // Create pet profile in backend if not already created
        if (!petId) {
          await createPetProfile(petName, imageDataUrl, angelImageUrl);
        }
        
        setShowInteractions(true);
        resolve();
      };
      
      img.src = imageDataUrl;
    });
  };

  const createPetProfile = async (name, originalImage, angelImage) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/pets`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: name,
          original_image: originalImage,
          angel_image: angelImage,
          user_id: user?.id || null // Add user ID to link pets to users (null safe)
        })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Pet profile created:', data);
        setPetId(data.pet_id);
        
        // Save to localStorage for persistence
        localStorage.setItem('petHeavenPetId', data.pet_id);
        localStorage.setItem('petHeavenPetName', name);
        localStorage.setItem('petHeavenAngelImage', angelImage);
        
        // Add to pet cache for consistency
        addPetToCache({
          id: data.pet_id,
          name: name,
          angel_image: angelImage,
          original_image: originalImage
        });
        
        // Load existing letters
        await loadLetters(data.pet_id);
      } else {
        console.error('Failed to create pet profile');
      }
    } catch (error) {
      console.error('Error creating pet profile:', error);
    }
  };

  const loadLetters = async (petIdToLoad) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/pets/${petIdToLoad}/letters`);
      if (response.ok) {
        const data = await response.json();
        setLetters(data.letters || []);
      }
    } catch (error) {
      console.error('Error loading letters:', error);
    }
  };

  const checkForAngelResponses = async (petIdToCheck) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/pets/${petIdToCheck}/angel-responses`);
      if (response.ok) {
        const data = await response.json();
        const newResponses = data.responses || [];
        
        // Check if there are new responses compared to what we have
        const currentResponseIds = angelResponses.map(r => r.id);
        const newResponsesFiltered = newResponses.filter(r => !currentResponseIds.includes(r.id));
        
        // Update all responses - unread count will be calculated automatically
        setAngelResponses(newResponses);
        
        // Only show notification for truly NEW messages that arrive during polling
        // Skip notification if: initial load OR we don't have any messages loaded yet
        // This ensures we only notify when a new message arrives while user is actively using the app
        const shouldShowNotification = newResponsesFiltered.length > 0 && 
                                        !isInitialAngelResponseLoad.current && 
                                        angelResponses.length > 0;
        
        if (shouldShowNotification) {
          // Show success message for new responses
          if (newResponsesFiltered.length === 1) {
            setSuccessMessage({
              title: 'Angel Response Received! 💌✨',
              description: `Angel ${petName} has sent you a loving message! Check your messages to read their response.`
            });
          } else {
            setSuccessMessage({
              title: 'Angel Responses Received! 💌✨',
              description: `Angel ${petName} has sent you ${newResponsesFiltered.length} loving messages! Check your messages to read their responses.`
            });
          }
          setShowSuccessMessage(true);
          // Auto-hide success message after 5 seconds
          setTimeout(() => {
            setShowSuccessMessage(false);
          }, 5000);
        }
        
        // Mark initial load as complete after first successful fetch
        if (isInitialAngelResponseLoad.current) {
          isInitialAngelResponseLoad.current = false;
        }
      }
    } catch (error) {
      console.error('Error checking for angel responses:', error);
    }
  };

  const markResponseAsRead = async (responseId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/pets/${petId}/angel-responses/${responseId}/mark-read`, {
        method: 'POST'
      });
      
      if (response.ok) {
        // Update local state to mark as read - unread count will update automatically
        setAngelResponses(prev => prev.map(r => 
          r.id === responseId ? { ...r, read: true } : r
        ));
      }
    } catch (error) {
      console.error('Error marking response as read:', error);
    }
  };

  const deleteAngelResponse = (responseId) => {
    // Show custom confirmation modal instead of window.confirm
    setDeleteConfirmation({ show: true, responseId });
  };

  const confirmDeleteAngelResponse = async () => {
    const responseId = deleteConfirmation.responseId;
    setDeleteConfirmation({ show: false, responseId: null });
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/pets/${petId}/angel-responses/${responseId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        // Remove from local state
        setAngelResponses(prev => prev.filter(r => r.id !== responseId));
        setSuccessMessage({
          title: 'Message Deleted',
          description: 'The angel message has been removed.'
        });
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);
      }
    } catch (error) {
      console.error('Error deleting angel response:', error);
      setSuccessMessage({
        title: 'Error',
        description: 'Failed to delete message. Please try again.'
      });
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
    }
  };

  const cancelDeleteAngelResponse = () => {
    setDeleteConfirmation({ show: false, responseId: null });
  };

  // Add letter to Memorial Garden
  const addLetterToMemorial = async (letter) => {
    try {
      const memory = {
        id: `letter_${letter.id}_${Date.now()}`,
        type: 'love_letter',
        title: `Love Letter to ${petName}`,
        text: letter.content,
        image: '/assets/love-letter-icon.svg',
        date: letter.timestamp || letter.created_at || letter.date || new Date().toISOString().split('T')[0],
        timestamp: new Date().toISOString(),
        source: 'love_letter',
        originalId: letter.id
      };

      const response = await fetch(`${BACKEND_URL}/api/pets/${petId}/memories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(memory)
      });

      if (response.ok) {
        setSuccessMessage({
          title: 'Letter Saved! 💌',
          description: 'Your love letter has been added to the Memorial Garden'
        });
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 4000);
      } else {
        throw new Error('Failed to save letter to Memorial Garden');
      }
    } catch (error) {
      console.error('Error saving letter to Memorial Garden:', error);
      alert('Failed to save letter to Memorial Garden. Please try again.');
    }
  };

  // Add angel message to Memorial Garden
  const addAngelMessageToMemorial = async (response) => {
    try {
      const memory = {
        id: `angel_message_${response.id}_${Date.now()}`,
        type: 'angel_message',
        title: `Message from Angel ${petName}`,
        text: response.content,
        image: '/assets/angel-message-icon.svg',
        date: response.timestamp || response.created_at || response.date || new Date().toISOString().split('T')[0],
        timestamp: new Date().toISOString(),
        source: 'angel_message',
        originalId: response.id
      };

      const response_api = await fetch(`${BACKEND_URL}/api/pets/${petId}/memories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(memory)
      });

      if (response_api.ok) {
        setSuccessMessage({
          title: 'Angel Message Saved! ✨',
          description: 'The angel message has been added to the Memorial Garden'
        });
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 4000);
      } else {
        throw new Error('Failed to save angel message to Memorial Garden');
      }
    } catch (error) {
      console.error('Error saving angel message to Memorial Garden:', error);
      alert('Failed to save angel message to Memorial Garden. Please try again.');
    }
  };

  // Add reflection to Memorial Garden
  const addReflectionToMemorial = async (memory) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/pets/${petId}/memories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(memory)
      });

      if (response.ok) {
        setSuccessMessage({
          title: 'Reflection Saved! 💭',
          description: 'Your daily reflection has been added to the Memorial Garden'
        });
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 4000);
      } else {
        throw new Error('Failed to save reflection to Memorial Garden');
      }
    } catch (error) {
      console.error('Error saving reflection to Memorial Garden:', error);
      alert('Failed to save reflection to Memorial Garden. Please try again.');
    }
  };

  const drawAngelWings = (ctx, width, height) => {
    const centerX = width / 2;
    const centerY = height / 2;
    
    ctx.save();
    ctx.globalAlpha = 0.7;
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.8)';
    ctx.lineWidth = 3;
    
    // Left wing
    ctx.beginPath();
    ctx.ellipse(centerX - 80, centerY, 40, 80, Math.PI / 6, 0, 2 * Math.PI);
    ctx.stroke();
    
    // Right wing
    ctx.beginPath();
    ctx.ellipse(centerX + 80, centerY, 40, 80, -Math.PI / 6, 0, 2 * Math.PI);
    ctx.stroke();
    
    ctx.restore();
  };

  const drawHalo = (ctx, x, y) => {
    ctx.save();
    ctx.globalAlpha = 0.6;
    ctx.strokeStyle = 'rgba(255, 223, 0, 0.8)';
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.arc(x, y, 30, 0, 2 * Math.PI);
    ctx.stroke();
    ctx.restore();
  };

  const sendTreat = async (selectedTreat) => {
    // OPTIMISTIC UI: Start animation immediately for instant feedback
    setTreatAnimation({
      treat: selectedTreat,
      timestamp: Date.now()
    });

    try {
      // Background API call with keepalive - continues even if user navigates away
      const response = await fetch(`${BACKEND_URL}/api/pets/${petId}/flowers?treat_type=${selectedTreat.id}&user_id=${user.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        keepalive: true
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Treat persisted to backend:', data);
        
        // Generate reaction message to return to the SendTreatsModal
        // The modal will display this inline instead of on the Home page
        const treatReactiveMessages = {
          chicken_jerky: [
            "That jerky was divine! 😋\nJust like the ones from home!",
            "Mmm, so savory and perfect! ✨\nYou know exactly what I love!",
            "Crunchy heaven in my mouth! 🌟\nThank you, sweet hooman!"
          ],
          tuna_puff: [
            "Fresh ocean breeze in every bite! 🌊\nThat tuna puff was magical!",
            "Fluffy clouds of fishy goodness! ☁️\nMy whiskers are tingling with joy!",
            "Swimming in flavor paradise! 🐟\nYou spoil me so wonderfully!"
          ],
          lamb_bite: [
            "Tender lamb from the celestial fields! 🌿\nAbsolutely scrumptious!",
            "Heavenly herbs and perfect texture! 🌟\nMy taste buds are dancing!",
            "Angelic lamb that melts my heart! 💕\nYou're the best treat-giver ever!"
          ],
          pumpkin_biscuit: [
            "Autumn magic in every crumb! 🍂\nThese pumpkin biscuits are heavenly!",
            "Golden sunset flavors! 🌅\nSpiced to perfection, dear hooman!",
            "Harvest moon sweetness! 🎃\nI'm purring with pumpkin bliss!"
          ],
          yogurt_drop: [
            "Creamy cloud kisses! ☁️\nThese yogurt drops are pure angel food!",
            "Smooth silk on my tongue! ✨\nLike drinking liquid starlight!",
            "Heavenly cream dreams! 💭\nYou know the way to my heart!"
          ]
        };

        const messages = treatReactiveMessages[selectedTreat.id] || ["That treat was amazing! Thank you! 💕"];
        const randomReactiveMessage = messages[Math.floor(Math.random() * messages.length)];
        
        // Return the result with reaction message for the SendTreatsModal to display inline
        // NO longer setting reactiveMessage here - the thank-you is now shown in the modal itself
        console.log('Treat sent successfully:', data);
        
        return {
          success: true,
          reactionMessage: randomReactiveMessage,
          data: data
        };
      } else {
        console.error('Failed to send treat');
        throw new Error('Failed to send treat');
      }
    } catch (error) {
      console.error('Error sending treat:', error);
      throw error;
    }
  };

  const handleTreatAnimationComplete = () => {
    setTreatAnimation(null);
  };

  const handlePetReactionComplete = () => {
    setPetReaction(null);
  };

  const sendLetter = async () => {
    if (currentLetter.trim() && !loading) {
      setLoading(true);
      try {
        const response = await fetch(`${BACKEND_URL}/api/pets/${petId}/letters`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            content: currentLetter,
            user_id: user.id
          }),
          keepalive: true  // Ensures request completes even if page unloads
        });

        if (response.ok) {
          const data = await response.json();
          setLetters(prev => [...prev, data.letter]);
          setCurrentLetter('');
          setHomeView('main');
          console.log('Letter sent successfully');

          // Show success message with angel response timing info
          if (data.message) {
            setSuccessMessage({
              title: 'Letter Sent Successfully! 💌',
              description: data.message
            });
            setShowSuccessMessage(true);
            // Auto-hide success message after 4 seconds
            setTimeout(() => {
              setShowSuccessMessage(false);
            }, 4000);
          }

          // Set reactive message for letter
          const letterReactiveMessages = [
            "Your words float like gentle stardust to my heart! 💫\nI feel your love with every word you send!",
            "I felt every emotion in your beautiful words! 💕\nThank you for sharing your heart with me!",
            "Your love letters make my halo glow brighter! ✨\nI treasure each word you send my way!",
            "Reading your thoughts brings me such joy! 🌈\nYour love reaches me across all of heaven!",
            "Every sentence is a hug from your heart! 🤗\nI save each letter in my celestial memory!",
            "Your words are like rainbow poetry! 🌈\nThey paint the clouds with your beautiful love!",
            "I can feel your heartbeat in every word! 💓\nYour letters are my favorite heavenly treasure!"
          ];

          const randomLetterMessage = letterReactiveMessages[Math.floor(Math.random() * letterReactiveMessages.length)];
          
          // Show reactive message after a short delay
          setTimeout(() => {
            setReactiveMessage(randomLetterMessage);
          }, 1500);
        } else {
          console.error('Failed to send letter');
          alert('Failed to send letter. Please try again.');
        }
      } catch (error) {
        console.error('Error sending letter:', error);
        alert('Failed to send letter. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  // Handle journal tile clicks
  const handleJournalTileClick = (action) => {
    switch (action) {
      case 'writeToYourPet':
        setJournalView('healing');
        break;
      case 'reflectOnToday':
        setJournalView('reflect');
        break;
      case 'recordADream':
      case 'memoryByDate':
        // For now, these launch the Five Stage Journal
        setJournalView('healing');
        break;
      default:
        setJournalView('healing');
    }
  };

  // Navigation bar icons (now using custom images)
  const navItems = [
    { key: 'home', label: 'Home', icon: '/icons/home.png' },
    { key: 'journal', label: 'Journal', icon: '/icons/journal.png' },
    { key: 'breathe', label: 'Breathe', icon: '/icons/breathe.png' },
    { key: 'memorial', label: 'Memorial', icon: '/icons/memorial.png' },
  ];

  // ✅ NOW we can do conditional returns after all hooks and functions are declared
  // Show loading screen while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-pink-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Handle auth navigation
  const handleAuthNavigate = (view) => {
    setAuthView(view);
  };

  // Show password reset if in recovery mode
  if (isPasswordRecovery) {
    return <ResetPassword onNavigate={handleAuthNavigate} />;
  }

  // Show appropriate auth screen if not authenticated
  if (!user) {
    switch (authView) {
      case 'signup':
        return <SignUp onNavigate={handleAuthNavigate} />;
      case 'forgot-password':
        return <ForgotPassword onNavigate={handleAuthNavigate} />;
      case 'login':
      default:
        return <Login onNavigate={handleAuthNavigate} />;
    }
  }

  return (
    <div className="app">
      {/* Floating Settings Button - Only show on main views */}
      {((activeSection === 'home' && homeView === 'main') ||
        (activeSection === 'journal' && journalView === 'main') ||
        (activeSection === 'breathe' && breatheView === 'main') ||
        activeSection === 'memorial') && (
        <div className="fixed top-4 right-4 z-30">
          <UserProfile />
        </div>
      )}

      <div className="heaven-background">
        {/* Floating clouds - Configuration-based approach */}
        {(() => {
          const cloudConfig = [
            { top: '8%', left: '1%',  width: 'w-24', height: 'h-20', opacity: 'opacity-45', delay: '0s',    duration: '22s', zIndex: 1 },
            { top: '13%', left: '20%', width: 'w-20', height: 'h-16', opacity: 'opacity-50', delay: '-2s',  duration: '20s', zIndex: 1 },
            { top: '2%', left: '32%', width: 'w-16', height: 'h-12', opacity: 'opacity-25', delay: '-4s',  duration: '26s', zIndex: 1 },
            { top: '9%', left: '56%', width: 'w-20', height: 'h-16', opacity: 'opacity-55', delay: '-6s',  duration: '24s', zIndex: 1 },
            { top: '25%', left: '8%', width: 'w-16', height: 'h-14', opacity: 'opacity-38', delay: '-8s',  duration: '23s', zIndex: 1 },
            { top: '0%', left: '27%', width: 'w-24', height: 'h-16', opacity: 'opacity-48', delay: '-12s', duration: '27s', zIndex: 1 },
            { top: '1%', left: '68%', width: 'w-20', height: 'h-16', opacity: 'opacity-36', delay: '-14s', duration: '22s', zIndex: 1 }
          ];
          

          return cloudConfig.map((cloud, idx) => (
            <img
              key={idx}
              src="/assets/cloud1.png"
              alt={`Cloud ${idx + 1}`}
              className={`absolute ${cloud.width} ${cloud.height} ${cloud.opacity} animate-float-slow`}
              style={{
                top: cloud.top,
                left: cloud.left,
                right: cloud.right,
                animationDelay: cloud.delay,
                animationDuration: cloud.duration,
                zIndex: cloud.zIndex
              }}
            />
          ));
        })()}
        
        <div className="container">
          {/* Header removed to prevent blocking of close buttons */}

          {/* Main Section Switcher */}
          {activeSection === 'home' && (
            <div className="heaven-background">
              <div className="container min-h-[calc(100vh-96px)]">
                {homeView === 'main' ? (
                  !angelPet ? (
              <div className="upload-section">
                <div className="upload-card">
                  <h2>Begin Your Rainbow Bridge Search</h2>
                  <p>Upload a photo and tell us your pet's name. We'll search the Rainbow Bridge to help you reunite…</p>
                  <input
                    id="pet-name"
                    name="pet-name"
                    type="text"
                    placeholder="What was your pet's name?"
                    value={petName}
                    onChange={(e) => setPetName(e.target.value)}
                    className="pet-name-input"
                  />
                  <div className="upload-area" onClick={() => fileInputRef.current?.click()}>
                    {loading ? (
                      <div>
                        <div className="upload-icon"><Icon name="sparkle" size="xl" animate /></div>
                        <p>Searching the Rainbow Bridge…</p>
                        <small>Your love is calling out across the clouds…</small>
                      </div>
                    ) : (
                      <div>
                        <div className="upload-icon"><Icon name="camera" size="xl" /></div>
                        <p>Click to upload your pet's photo</p>
                        <small>We'll help you find them across the Rainbow Bridge.</small>
                      </div>
                    )}
                    <input
                      id="pet-image-upload"
                      name="pet-image-upload"
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      style={{ display: 'none' }}
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="heaven-home">
                {/* Emotionally Welcoming Angel Pet Section */}
                <div className="angel-pet-display relative">
                  {/* Admin Controls - Subtle and tucked away */}
                  {/* <div className="absolute top-2 right-2 flex gap-2" style={{ zIndex: 9999 }}>
                    <button 
                      onClick={() => {
                        clearLocalStorage();
                        window.location.reload();
                      }}
                      className="btn-new-pet text-xs px-3 py-2 bg-white/80 backdrop-blur-sm rounded-full text-gray-700 hover:bg-white/90 transition-all duration-200 shadow-lg border border-white/50"
                      title="Create a new angel pet"
                      style={{ pointerEvents: 'auto' }}
                    >
                      New Pet
                    </button>
                    <button 
                      onClick={() => {
                        clearLocalStorage();
                        setAngelPet(null);
                        setShowInteractions(false);
                        setPetId(null);
                        setPetName('');
                        console.log('🧹 Storage cleared - ready for fresh AI transformation!');
                      }}
                      className="btn-clear-storage text-xs px-3 py-2 bg-white/80 backdrop-blur-sm rounded-full text-gray-700 hover:bg-white/90 transition-all duration-200 shadow-lg border border-white/50"
                      title="Clear storage and start fresh"
                      style={{ pointerEvents: 'auto' }}
                    >
                      Reset
                    </button>
                  </div> */}

                  {/* Main Welcome Bubble Component with Dual-Layer Speech */}
                  <WelcomeBubble 
                    petName={petName} 
                    angelPet={angelPet} 
                    reactiveMessage={reactiveMessage}
                    onReactiveMessageComplete={() => setReactiveMessage(null)}
                  />

                </div>
                {showInteractions && (
                  <div className="interactions px-4 mt-6">
                    <div className="interaction-buttons flex flex-col sm:flex-row gap-3 max-w-md mx-auto justify-center">
                      <PastelButton 
                        onClick={() => setHomeView('treats')} 
                        color="rose"
                        className="flex-1"
                      >
                        Send Heavenly Treats <img src="/assets/givetreats.png" alt="Give treats" className="inline w-6 h-6 ml-1" />
                      </PastelButton>
                      <PastelButton 
                        onClick={() => setHomeView('letter')} 
                        color="sky"
                        className="flex-1"
                      >
                        Write a Love Letter ✉️
                      </PastelButton>
                      <PastelButton 
                        onClick={() => setHomeView('messages')} 
                        color="purple"
                        className="flex-1 relative"
                      >
                        Angel Messages 💌
                        {unreadCount > 0 && (
                          <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                            {unreadCount}
                          </span>
                        )}
                      </PastelButton>
                    </div>
                  </div>
                )}
                {/* Floating treats */}
                {flowers.map(flower => (
                  <div
                    key={flower.id}
                    className="floating-flower"
                    style={{ left: `${flower.x}%`, top: `${flower.y}%` }}
                  >
                    <img src="/assets/treats.png" alt="Treat" className="w-12 h-12" />
                  </div>
                ))}
              </div>
            )) : homeView === 'letter' ? renderHomeLetterView() : 
                 homeView === 'treats' ? renderHomeTreatsView() : 
                 homeView === 'messages' ? renderHomeMessagesView() : 
                 null}
              </div>
            </div>
          )}
          {activeSection === 'journal' && (
            <div className="heaven-background">
              <div className="container min-h-[calc(100vh-96px)]">
                {journalView === 'main' ? renderJournalMainView() : 
                 journalView === 'healing' ? renderJournalHealingView() : 
                 journalView === 'reflect' ? renderJournalReflectView() :
                 renderJournalMainView()}
              </div>
            </div>
          )}
          {activeSection === 'breathe' && (
            <div className="heaven-background">
              <div className="container min-h-[calc(100vh-96px)]">
                {breatheView === 'main' ? renderBreatheMainView() : 
                 breatheView === 'exercise' ? renderBreatheExerciseView() : 
                 renderBreatheMainView()}
                </div>
            </div>
          )}
          {activeSection === 'memorial' && (
            <Memorial
              petName={petName}
              petId={petId}
            />
          )}


          {/* Success Message */}
          {showSuccessMessage && (
            <div className="success-notification">
              <div className="success-content">
                <span className="success-icon"><Icon name="success" size="md" /></span>
                <div className="success-text">
                  <h4>{successMessage.title}</h4>
                  <p>{successMessage.description}</p>
                </div>
              </div>
            </div>
          )}

          {/* Delete Confirmation Modal */}
          {deleteConfirmation.show && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              {/* Backdrop */}
              <div 
                className="absolute inset-0 bg-black/30 backdrop-blur-sm"
                onClick={cancelDeleteAngelResponse}
              />
              
              {/* Modal */}
              <div className="relative bg-white/90 backdrop-blur-md rounded-3xl p-6 shadow-[0_20px_50px_rgba(0,0,0,0.15)] border border-white/50 max-w-sm w-full mx-4 animate-fade-in">
                {/* Inner highlight */}
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-white/50 via-white/20 to-transparent pointer-events-none" />
                
                <div className="relative z-10">
                  {/* Warning Icon */}
                  <div className="flex justify-center mb-4">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-red-100 to-rose-100 flex items-center justify-center shadow-md">
                      <span className="text-2xl">⚠️</span>
                    </div>
                  </div>
                  
                  {/* Title */}
                  <h3 className="text-lg font-bold text-gray-700 text-center mb-2">
                    Delete Message?
                  </h3>
                  
                  {/* Description */}
                  <p className="text-gray-600 text-sm text-center leading-relaxed mb-6">
                    This angel message will be permanently deleted. This action cannot be undone.
                  </p>
                  
                  {/* Buttons */}
                  <div className="flex gap-3">
                    <button
                      onClick={cancelDeleteAngelResponse}
                      className="flex-1 px-4 py-3 bg-white/60 backdrop-blur-sm text-gray-600 rounded-xl font-medium hover:bg-white/80 hover:-translate-y-0.5 active:scale-[0.98] transition-all duration-200 border border-white/40 shadow-sm"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={confirmDeleteAngelResponse}
                      className="flex-1 px-4 py-3 bg-gradient-to-r from-red-400 to-rose-500 text-white rounded-xl font-medium hover:from-red-500 hover:to-rose-600 hover:-translate-y-0.5 active:scale-[0.98] transition-all duration-200 shadow-md"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}



          {treatAnimation && (
            <TreatAnimation
              treat={treatAnimation.treat}
              angelPetPosition={{ x: 50, y: 40 }} // Position of angel pet on screen
              onAnimationComplete={handleTreatAnimationComplete}
            />
          )}


        </div>
      </div>

      
      {/* Hidden canvas for image processing */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />

      {/* Bottom Navigation Bar */}
      <nav className="bottom-nav">
        {navItems.map((item) => (
          <button
            key={item.key}
            className={`nav-btn${activeSection === item.key ? ' active' : ''}`}
            onClick={() => setActiveSection(item.key)}
          >
            <img src={item.icon} alt={item.label} className="nav-icon" />
            <span className="nav-label">{item.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}

// Main App Component with AuthProvider wrapper
function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;